import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

import {
  buildDispatchAppendNewLegCommand,
  DispatchManifestLegValidationError,
} from '../../adapters/dispatch-manifest-leg.adapter';
import { DispatchFreightApiService } from '../../data/dispatch-freight-api.service';
import {
  assertDispatchManifestDetail,
  buildDispatchManifestIdempotencyKey,
  canonicalDispatchJson,
  dispatchManifestPredecessorOptions,
  DispatchManifestSelectionError,
  selectDispatchManifestPredecessor,
} from '../../domain/dispatch-manifest-append';
import type {
  DispatchAppendNewLegDetails,
  DispatchAppendNewLegCommand,
  DispatchAppendNewLegResponse,
  DispatchManifestDetailLeg,
  DispatchManifestDetailResponse,
  DispatchManifestTarget,
  DispatchManifestTargetsResponse,
  DispatchRatingRequest,
} from '../../models/dispatch-freight.models';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';
type AppendState = 'idle' | 'refreshing' | 'appending' | 'success' | 'conflict' | 'error';

interface PendingAppendRetry {
  command: DispatchAppendNewLegCommand;
  idempotencyKey: string;
  inputFingerprint: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-dispatch-manifest-append',
  templateUrl: './dispatch-manifest-append.component.html',
  styleUrls: ['./dispatch-manifest-append.component.scss'],
})
export class DispatchManifestAppendComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) customerContactUuid = '';
  @Input() ratingRequest: DispatchRatingRequest | null = null;
  @Input() appendDetails: DispatchAppendNewLegDetails = {};

  searchText = '';
  targets: DispatchManifestTarget[] = [];
  targetState: LoadState = 'idle';
  targetError = '';

  selectedManifestUuid = '';
  detail: DispatchManifestDetailResponse | null = null;
  detailState: LoadState = 'idle';
  detailError = '';
  selectedPredecessorUuid = '';

  appendState: AppendState = 'idle';
  appendMessage = '';
  appendResult: DispatchAppendNewLegResponse | null = null;

  private readonly searchChanges = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private targetSubscription: Subscription | null = null;
  private detailSubscription: Subscription | null = null;
  private appendRefreshSubscription: Subscription | null = null;
  private appendSubscription: Subscription | null = null;
  private requestSequence = 0;
  private pendingRetry: PendingAppendRetry | null = null;
  private ambiguousReviewRequired = false;
  private appendDetailsFingerprint = '';
  private ratingRequestFingerprint = '';

  constructor(private readonly api: DispatchFreightApiService) {}

  ngOnInit(): void {
    this.subscriptions.add(this.searchChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
    ).subscribe((search) => this.loadTargets(search)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerContactUuid']) {
      this.resetForCustomer();
    }
    if (changes['ratingRequest'] && !changes['ratingRequest'].firstChange) {
      const fingerprint = this.inputValueFingerprint(this.ratingRequest);
      if (fingerprint !== this.ratingRequestFingerprint) {
        const hadPendingRetry = this.pendingRetry !== null;
        this.invalidatePendingRetry('The rated freight changed, so the previous ambiguous append cannot be retried. Refresh and review the manifest before another append.');
        if (this.appendState !== 'appending') {
          this.cancelAppendOperation();
          if (!hadPendingRetry) this.clearAppendOutcome();
        }
      }
      this.ratingRequestFingerprint = fingerprint;
    } else if (changes['ratingRequest']) {
      this.ratingRequestFingerprint = this.inputValueFingerprint(this.ratingRequest);
    }
    if (changes['appendDetails']) {
      const fingerprint = this.inputValueFingerprint(this.appendDetails);
      if (!changes['appendDetails'].firstChange && fingerprint !== this.appendDetailsFingerprint) {
        this.invalidatePendingRetry('The load details changed, so the previous ambiguous append cannot be retried. Refresh and review the manifest before another append.');
      }
      this.appendDetailsFingerprint = fingerprint;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.targetSubscription?.unsubscribe();
    this.detailSubscription?.unsubscribe();
    this.appendRefreshSubscription?.unsubscribe();
    this.appendSubscription?.unsubscribe();
  }

  get customerReady(): boolean {
    return UUID_PATTERN.test(this.customerContactUuid.trim());
  }

  get predecessorOptions(): DispatchManifestDetailLeg[] {
    return this.detail ? dispatchManifestPredecessorOptions(this.detail) : [];
  }

  get appendBusy(): boolean {
    return this.appendState === 'refreshing' || this.appendState === 'appending';
  }

  get appendDisabled(): boolean {
    return this.appendBusy
      || !this.ratingRequest
      || this.detailState !== 'ready'
      || !this.selectedManifestUuid
      || !this.selectedPredecessorUuid
      || this.ambiguousReviewRequired;
  }

  get appendPrerequisite(): string {
    if (!this.customerReady) return 'Select a customer before searching ladings.';
    if (!this.selectedManifestUuid) return 'Choose an existing lading.';
    if (this.detailState === 'loading') return 'Loading the latest lading details.';
    if (this.detailState !== 'ready') return 'Load valid lading details.';
    if (!this.selectedPredecessorUuid) return 'Choose which active leg this freight follows.';
    if (!this.ratingRequest) return 'Get a customer rate for this freight first.';
    if (this.ambiguousReviewRequired) return 'The freight changed after the last request. Refresh the lading and review it before trying again.';
    if (this.pendingRetry) return 'The last result could not be confirmed. A safe retry will send the exact same request.';
    return 'Ready. The lading will be checked for changes before the new leg is added.';
  }

  get hasPendingRetry(): boolean {
    return this.pendingRetry !== null;
  }

  onSearchInput(event: Event): void {
    if (this.appendBusy) return;
    this.searchText = (event.target as HTMLInputElement).value;
    this.searchChanges.next(this.searchText);
  }

  refreshTargets(): void {
    if (this.appendBusy) return;
    this.loadTargets(this.searchText);
  }

  selectTarget(target: DispatchManifestTarget): void {
    if (this.appendBusy) return;
    this.cancelAppendOperation();
    this.pendingRetry = null;
    this.ambiguousReviewRequired = false;
    this.clearAppendOutcome();
    this.selectedManifestUuid = target.manifest_uuid.toLowerCase();
    this.detail = null;
    this.selectedPredecessorUuid = '';
    this.loadDetail(target.manifest_uuid, target.predecessor_lading_uuid);
  }

  refreshDetail(): void {
    if (this.appendBusy || !this.selectedManifestUuid) return;
    this.cancelAppendOperation();
    this.pendingRetry = null;
    this.ambiguousReviewRequired = false;
    this.clearAppendOutcome();
    this.loadDetail(this.selectedManifestUuid, this.selectedPredecessorUuid);
  }

  onPredecessorChange(event: Event): void {
    if (this.appendBusy) return;
    const hadPendingRetry = this.pendingRetry !== null;
    this.invalidatePendingRetry('The predecessor changed after an ambiguous response. Refresh and inspect the manifest before another append.');
    const value = (event.target as HTMLSelectElement).value.toLowerCase();
    this.selectedPredecessorUuid = this.predecessorOptions.some(
      (leg) => leg.lading_uuid.toLowerCase() === value,
    ) ? value : '';
    if (!hadPendingRetry) this.clearAppendOutcome();
  }

  appendLeg(): void {
    if (this.appendBusy) return;
    if (this.ambiguousReviewRequired) {
      this.appendState = 'error';
      this.appendMessage = this.appendPrerequisite;
      return;
    }
    if (!this.ratingRequest || !this.selectedManifestUuid || !this.selectedPredecessorUuid) {
      this.appendState = 'error';
      this.appendMessage = this.appendPrerequisite;
      return;
    }

    if (this.pendingRetry) {
      const retry = this.pendingRetry;
      if (retry.inputFingerprint !== this.currentAppendInputFingerprint()) {
        this.pendingRetry = null;
        this.ambiguousReviewRequired = true;
        this.appendState = 'error';
        this.appendMessage = 'The freight, load details, customer, manifest, or predecessor changed after an ambiguous response. The old request will not be retried. Refresh and inspect the manifest before proceeding.';
        return;
      }

      this.cancelAppendOperation();
      const retrySequence = ++this.requestSequence;
      this.sendAppendCommand(retrySequence, retry.command, retry.idempotencyKey, retry.inputFingerprint, true);
      return;
    }

    this.cancelAppendOperation();
    const sequence = ++this.requestSequence;
    const manifestUuid = this.selectedManifestUuid;
    const predecessorUuid = this.selectedPredecessorUuid;
    this.appendState = 'refreshing';
    this.appendMessage = 'Checking the latest lading and route…';
    this.appendResult = null;

    this.appendRefreshSubscription = this.api.manifestDetail(manifestUuid).subscribe({
      next: (response) => {
        void this.continueAppend(sequence, manifestUuid, predecessorUuid, response);
      },
      error: (error: HttpErrorResponse) => {
        if (sequence !== this.requestSequence) return;
        this.appendState = 'error';
        this.appendMessage = this.httpErrorText(error, 'Unable to check the lading before adding freight.');
      },
    });
  }

  trackTarget(_index: number, target: DispatchManifestTarget): string {
    return target.manifest_uuid;
  }

  trackLeg(_index: number, leg: DispatchManifestDetailLeg): string {
    return leg.lading_uuid;
  }

  isTargetSelected(target: DispatchManifestTarget): boolean {
    return target.manifest_uuid.toLowerCase() === this.selectedManifestUuid;
  }

  targetReference(target: DispatchManifestTarget): string {
    return target.anchor_lading.lading_id
      || target.anchor_lading.lading_number
      || target.anchor_lading.alternate_reference
      || `Shipment ${target.manifest_id}`;
  }

  laneLabel(party: { name: string | null; locality: string | null; region: string | null }): string {
    return [party.name, party.locality, party.region].filter(Boolean).join(' · ') || 'Unknown';
  }

  legLabel(leg: DispatchManifestDetailLeg): string {
    const identity = leg.lading_id || leg.lading_number || leg.lading_uuid.slice(0, 8);
    const sequence = leg.manifest_leg_sequence === null ? 'legacy' : `leg ${leg.manifest_leg_sequence}`;
    return `${sequence} · ${identity} · ${this.laneLabel(leg.consignee)}`;
  }

  private resetForCustomer(): void {
    this.targetSubscription?.unsubscribe();
    this.detailSubscription?.unsubscribe();
    this.cancelAppendOperation();
    this.searchText = '';
    this.targets = [];
    this.targetState = 'idle';
    this.targetError = '';
    this.selectedManifestUuid = '';
    this.detail = null;
    this.detailState = 'idle';
    this.detailError = '';
    this.selectedPredecessorUuid = '';
    this.pendingRetry = null;
    this.ambiguousReviewRequired = false;
    this.clearAppendOutcome();
    if (this.customerReady) this.loadTargets('');
  }

  private loadTargets(search: string): void {
    if (this.appendBusy) return;
    if (!this.customerReady) {
      this.targets = [];
      this.targetState = 'idle';
      return;
    }

    const customerUuid = this.customerContactUuid.trim().toLowerCase();
    this.targetSubscription?.unsubscribe();
    this.targetState = 'loading';
    this.targetError = '';
    this.targetSubscription = this.api.manifestTargets({
      customer_contact_uuid: customerUuid,
      search,
      limit: 20,
    }).subscribe({
      next: (response) => {
        try {
          this.assertTargetScope(response, customerUuid);
          this.targets = response.data;
          this.targetState = 'ready';
        } catch (error: unknown) {
          this.targets = [];
          this.targetState = 'error';
          this.targetError = this.errorText(error, 'Manifest targets failed customer validation.');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.targets = [];
        this.targetState = 'error';
        this.targetError = this.httpErrorText(error, 'Unable to search this customer’s manifests.');
      },
    });
  }

  private loadDetail(manifestUuid: string, preferredPredecessor?: string | null): void {
    const customerUuid = this.customerContactUuid.trim().toLowerCase();
    this.detailSubscription?.unsubscribe();
    this.detailState = 'loading';
    this.detailError = '';
    this.detailSubscription = this.api.manifestDetail(manifestUuid).subscribe({
      next: (response) => {
        try {
          this.installDetail(response, manifestUuid, customerUuid, preferredPredecessor);
        } catch (error: unknown) {
          this.failDetail(error);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.detail = null;
        this.selectedPredecessorUuid = '';
        this.detailState = 'error';
        this.detailError = this.httpErrorText(error, 'Unable to load lading details.');
      },
    });
  }

  private async continueAppend(
    sequence: number,
    manifestUuid: string,
    predecessorUuid: string,
    freshDetail: DispatchManifestDetailResponse,
  ): Promise<void> {
    if (sequence !== this.requestSequence || !this.ratingRequest) return;

    try {
      assertDispatchManifestDetail(freshDetail, manifestUuid, this.customerContactUuid);
      const activePredecessor = dispatchManifestPredecessorOptions(freshDetail).some(
        (leg) => leg.lading_uuid.toLowerCase() === predecessorUuid,
      );
      this.installDetail(freshDetail, manifestUuid, this.customerContactUuid, predecessorUuid);
      if (!activePredecessor) {
        this.appendState = 'error';
        this.appendMessage = 'The selected predecessor changed or was voided. Review the refreshed legs and try again.';
        return;
      }

      const inputFingerprint = this.currentAppendInputFingerprint();
      const command = buildDispatchAppendNewLegCommand({
        manifest_uuid: manifestUuid,
        manifest_version: freshDetail.data.manifest.version,
        predecessor_lading_uuid: predecessorUuid,
      }, this.ratingRequest, this.appendDetails);
      const idempotencyKey = await buildDispatchManifestIdempotencyKey(command);
      if (sequence !== this.requestSequence) return;
      if (inputFingerprint !== this.currentAppendInputFingerprint()) {
        this.appendState = 'error';
        this.appendMessage = 'Freight or load details changed while the append command was being prepared. Nothing was sent; review and try again.';
        return;
      }
      this.sendAppendCommand(
        sequence,
        command,
        idempotencyKey,
        inputFingerprint,
        false,
      );
    } catch (error: unknown) {
      if (sequence !== this.requestSequence) return;
      this.appendState = 'error';
      this.appendMessage = this.errorText(error, 'The freight leg failed append validation.');
    }
  }

  private sendAppendCommand(
    sequence: number,
    command: DispatchAppendNewLegCommand,
    idempotencyKey: string,
    inputFingerprint: string,
    isRetry: boolean,
  ): void {
    this.appendState = 'appending';
    this.appendMessage = isRetry
      ? 'Safely retrying the exact same request…'
      : 'Adding the new freight leg…';
    this.appendSubscription = this.api.appendNewLeg(command, idempotencyKey).subscribe({
      next: (response) => {
        if (sequence !== this.requestSequence) return;
        this.pendingRetry = null;
        this.ambiguousReviewRequired = false;
        this.appendState = 'success';
        this.appendResult = response;
        this.appendMessage = `Lading ${response.data.lading_id ?? response.data.lading_uuid} appended as leg ${response.data.manifest_leg_sequence}.`;
        this.refreshDetailAfterOutcome(command.manifest_uuid, response.data.lading_uuid);
      },
      error: (error: HttpErrorResponse) => {
        if (sequence !== this.requestSequence) return;
        if (error.status === 409) {
          this.pendingRetry = null;
          this.ambiguousReviewRequired = false;
          this.appendState = 'conflict';
          this.appendMessage = 'The manifest changed before this append. Its current version is being refreshed; review it and submit again.';
          this.refreshDetailAfterOutcome(
            command.manifest_uuid,
            command.request.predecessor_lading_uuid ?? '',
          );
          return;
        }
        if (error.status === 0 || error.status >= 500) {
          this.pendingRetry = { command, idempotencyKey, inputFingerprint };
          this.appendState = 'error';
          this.appendMessage = 'The append outcome could not be confirmed. Retry will send the exact same body and idempotency key; it will not refresh or generate a new request.';
          return;
        }

        this.pendingRetry = null;
        this.ambiguousReviewRequired = false;
        this.appendState = 'error';
        this.appendMessage = this.httpErrorText(error, 'Unable to append the freight leg.');
      },
    });
  }

  private refreshDetailAfterOutcome(manifestUuid: string, preferredPredecessor: string): void {
    this.detailSubscription?.unsubscribe();
    this.detailState = 'loading';
    this.detailSubscription = this.api.manifestDetail(manifestUuid).subscribe({
      next: (response) => {
        try {
          this.installDetail(response, manifestUuid, this.customerContactUuid, preferredPredecessor);
        } catch (error: unknown) {
          this.failDetail(error);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.detailState = 'error';
        this.detailError = this.httpErrorText(error, 'The append result is known, but refreshed manifest detail could not be loaded.');
      },
    });
  }

  private installDetail(
    response: DispatchManifestDetailResponse,
    manifestUuid: string,
    customerUuid: string,
    preferredPredecessor?: string | null,
  ): void {
    const accepted = assertDispatchManifestDetail(response, manifestUuid, customerUuid);
    this.detail = accepted;
    this.detailState = 'ready';
    this.detailError = '';
    this.selectedPredecessorUuid = selectDispatchManifestPredecessor(
      accepted,
      preferredPredecessor,
    ) ?? '';
    if (!this.selectedPredecessorUuid) {
      this.detailState = 'error';
      this.detailError = 'This manifest has no active leg that can be used as a predecessor.';
    }
  }

  private failDetail(error: unknown): void {
    this.detail = null;
    this.selectedPredecessorUuid = '';
    this.detailState = 'error';
    this.detailError = this.errorText(error, 'Manifest detail failed customer validation.');
  }

  private assertTargetScope(response: DispatchManifestTargetsResponse, customerUuid: string): void {
    const metaCustomer = response.meta.customer_contact_uuid.trim().toLowerCase();
    const hasForeignTarget = response.data.some(
      (target) => target.customer.contact_uuid.trim().toLowerCase() !== customerUuid,
    );
    if (metaCustomer !== customerUuid || hasForeignTarget) {
      throw new DispatchManifestSelectionError('The target search returned data outside the active customer scope.');
    }
  }

  private cancelAppendOperation(): void {
    this.requestSequence += 1;
    this.appendRefreshSubscription?.unsubscribe();
    this.appendRefreshSubscription = null;
    this.appendSubscription?.unsubscribe();
    this.appendSubscription = null;
  }

  private invalidatePendingRetry(message: string): void {
    if (!this.pendingRetry) return;
    this.pendingRetry = null;
    this.ambiguousReviewRequired = true;
    this.appendState = 'error';
    this.appendResult = null;
    this.appendMessage = message;
  }

  private currentAppendInputFingerprint(): string {
    return canonicalDispatchJson({
      customer_contact_uuid: this.customerContactUuid.trim().toLowerCase(),
      manifest_uuid: this.selectedManifestUuid,
      predecessor_lading_uuid: this.selectedPredecessorUuid,
      rating_request: this.ratingRequest,
      append_details: this.appendDetails,
    });
  }

  private inputValueFingerprint(value: unknown): string {
    try {
      return canonicalDispatchJson(value);
    } catch {
      return '';
    }
  }

  private clearAppendOutcome(): void {
    this.appendState = 'idle';
    this.appendMessage = '';
    this.appendResult = null;
  }

  private errorText(error: unknown, fallback: string): string {
    if (error instanceof DispatchManifestLegValidationError) {
      return error.issues.map((issue) => issue.message).join(' ');
    }
    if (error instanceof Error && error.message.trim()) return error.message.trim();
    return fallback;
  }

  private httpErrorText(error: HttpErrorResponse, fallback: string): string {
    if (typeof error.error === 'string' && error.error.trim()) return error.error.trim();
    if (error.error && typeof error.error === 'object') {
      const payload = error.error as { message?: unknown; error?: unknown };
      if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
      if (typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
    }
    return error.message || fallback;
  }
}
